// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Switch.tsx
 * @input Uses React, useId, ChangeEvent, FieldLabel, FieldStatus, IconType, InputStatus, useTooltip
 * @output Exports Switch component, SwitchProps, SwitchLabelPosition, SwitchLabelSpacing
 * @position Core implementation; consumed by index.ts, tested by Switch.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Switch/Switch.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Switch/Switch.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Switch/index.ts (exports if types change)
 * - /apps/storybook/stories/Switch.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Switch/ (showcase blocks)
 */
import { useId, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { FieldLabel } from "../Field/FieldLabel.js";
import { FieldStatus } from "../FieldStatus/FieldStatus.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import "./switch.markers.stylex.js";
import { switchScope } from "./switch.markers.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";

// Fixed dimensions: 40px width, 24px height, 16px thumb (off), 20px thumb (on)
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const SWITCH_WIDTH = 40;
const SWITCH_HEIGHT = 24;
const THUMB_SIZE_OFF = 16;
const THUMB_SIZE_ON = 20;
const TRACK_PADDING = 4;
// Padding between thumb right edge and track inner edge when on
const ON_RIGHT_PADDING = 2;
// Travel distance for on state: positions thumb with ON_RIGHT_PADDING from right edge
const THUMB_TRAVEL_ON = SWITCH_WIDTH - TRACK_PADDING - THUMB_SIZE_ON - ON_RIGHT_PADDING;
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  containerSpread: {
    kjj79g: "x1qughib",
    kzqmXN: "xh8yej3",
    $$css: true
  }
};
// Dynamic field width (number -> px, string used as-is).
const dynamicWidthStyles = {
  width: width => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};

/**
 * A toggle switch component for boolean values.
 *
 * @example
 * ```
 * <Switch
 *   label="Enable notifications"
 *   value={enabled}
 *   onChange={setEnabled}
 * />
 * <Switch
 *   label="Dark mode"
 *   description="Switch to a darker color scheme"
 *   value={darkMode}
 *   onChange={setDarkMode}
 * />
 * ```
 */
export function Switch({
  label,
  isLabelHidden = false,
  description,
  onChange,
  changeAction,
  isLoading = false,
  value,
  isDisabled = false,
  htmlName,
  disabledMessage,
  isOptional = false,
  isRequired = false,
  onFocus,
  onBlur,
  labelIcon,
  labelTooltip,
  labelPosition = 'end',
  labelSpacing = 'hug',
  status,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;
  const isOn = optimisticValue === true;

  // 'default' is a deprecated alias for 'hug' (#2889).
  const resolvedLabelSpacing = labelSpacing === 'default' ? 'hug' : labelSpacing;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the switch row (which already exists) and the
  // native checkbox stays perceivable via aria-disabled instead of the disabled
  // attribute. Toggling is blocked by the isDisabled guard in onChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container row is not naturally focusable; focusin bubbles up from the
    // native input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // Build aria-describedby from description and status message
  // Only include descriptionID when the element actually renders.
  // FieldLabel renders the description (with descriptionID) even when the
  // label is visually hidden — it's sr-only, so keep it linked.
  const describedByParts = [];
  if (description) {
    describedByParts.push(descriptionID);
  }
  if (status?.message) {
    describedByParts.push(statusMessageID);
  }
  if (showsDisabledMessage) {
    describedByParts.push(disabledMessageTooltip.describedBy);
  }
  const ariaDescribedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;
  const switchElement = /*#__PURE__*/_jsxs("div", {
    ...{
      className: "x1n2onr6 x78zum5 x6s0dn4 x2lah0s x100vrsf xxk0z11 xc8icb0"
    },
    children: [/*#__PURE__*/_jsx("input", {
      ref: mergeRefs(ref, disabledMessageTooltip.positionRef),
      id: id,
      type: "checkbox",
      role: "switch"
      // Withhold the name while disabled: with a disabledMessage the
      // input stays focusable (not natively disabled), and a disabled
      // control must not submit.
      ,
      name: isDisabled ? undefined : htmlName,
      checked: isOn
      // With a disabledMessage the switch keeps focusability via aria-disabled
      // so the reason is focus-discoverable; toggling is still blocked by the
      // isDisabled guard in onChange below.
      ,
      disabled: isDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      required: isRequired,
      onChange: e => {
        if (isDisabled || isBusy) {
          return;
        }
        const checked = e.target.checked;
        onChange?.(checked, e);
        if (changeAction && !e.defaultPrevented) {
          startTransition(async () => {
            setOptimisticValue(checked);
            await changeAction(checked, e);
          });
        }
      },
      onFocus: onFocus,
      onBlur: onBlur,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": status?.type === 'error' ? true : undefined,
      "aria-busy": isBusy || undefined,
      ...{
        0: {
          className: "x10l6tqk x1ghz6dp x1717udv xg01cxk x1ypdohk x1vjfegm x100vrsf xxk0z11"
        },
        2: {
          className: "x10l6tqk x1ghz6dp x1717udv xg01cxk x1vjfegm x100vrsf xxk0z11 x1h6gzvc"
        },
        1: {
          className: "x10l6tqk x1ghz6dp x1717udv xg01cxk x1ypdohk x1vjfegm x100vrsf xxk0z11 x47corl"
        },
        3: {
          className: "x10l6tqk x1ghz6dp x1717udv xg01cxk x1vjfegm x100vrsf xxk0z11 x1h6gzvc x47corl"
        }
      }[!!isDisabled << 1 | !!isBusy << 0]
    }), /*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      ...mergeProps(themeProps('switch', {
        checked: isOn ? 'checked' : null,
        disabled: isDisabled ? 'disabled' : null
      }), {
        0: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui x13wei8l"
        },
        8: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1ewilqj x11ppjs"
        },
        4: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui x13wei8l x1a2a7pz x1gj1ihy x1edzap1"
        },
        12: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1ewilqj x11ppjs x1a2a7pz x1gj1ihy x1edzap1"
        },
        2: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui x13wei8l xbyyjgo"
        },
        10: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1ewilqj x11ppjs xbyyjgo"
        },
        6: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui x13wei8l x1a2a7pz x1gj1ihy x1edzap1 xbyyjgo"
        },
        14: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1ewilqj x11ppjs x1a2a7pz x1gj1ihy x1edzap1 xbyyjgo"
        },
        1: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui"
        },
        9: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xspzpui"
        },
        5: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1a2a7pz x1gj1ihy x1edzap1 xspzpui"
        },
        13: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1a2a7pz x1gj1ihy x1edzap1 xspzpui"
        },
        3: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xbyyjgo xspzpui"
        },
        11: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 xbyyjgo xspzpui"
        },
        7: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1a2a7pz x1gj1ihy x1edzap1 xbyyjgo xspzpui"
        },
        15: {
          className: "x78zum5 x6s0dn4 x100vrsf xxk0z11 xfawy5m xjspbzw x15406qy xuedmi6 x12w9bfk xlr8y92 x9f619 x1a2a7pz x1gj1ihy x1edzap1 xbyyjgo xspzpui"
        }
      }[!!isOn << 3 | !!!isDisabled << 2 | !!isDisabled << 1 | !!(isDisabled && !isOn) << 0]),
      children: /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('switch-thumb', {
          checked: isOn ? 'checked' : null
        }), {
          0: {
            className: "x78zum5 x6s0dn4 xl56j7k xjspbzw x10xzikg xh3q6i0 xuedmi6 x12w9bfk xlr8y92 x1kky2od xlup9mm xbryuvx"
          },
          1: {
            className: "x78zum5 x6s0dn4 xl56j7k xjspbzw x10xzikg xh3q6i0 xuedmi6 x12w9bfk xlr8y92 xw4jnvo x1qx5ct2 x15481yp"
          }
        }[!!isOn << 0]),
        children: isBusy && /*#__PURE__*/_jsx(Spinner, {
          size: "sm"
        })
      })
    }), isBusy && /*#__PURE__*/_jsx(VisuallyHidden, {
      role: "status",
      children: "Loading"
    })]
  });
  const labelElement = /*#__PURE__*/_jsx("div", {
    ...{
      className: "x78zum5 xdt5ytf x1lsbc85 xl56j7k xjwf9q1"
    },
    children: /*#__PURE__*/_jsx(FieldLabel, {
      label: label,
      inputID: id,
      isLabelHidden: isLabelHidden,
      isDisabled: isDisabled,
      isOptional: isOptional,
      isRequired: isRequired,
      labelIcon: labelIcon,
      labelTooltip: labelTooltip,
      description: description,
      descriptionID: descriptionID
    })
  });
  return /*#__PURE__*/_jsxs("div", {
    ...mergeProps(themeProps('switch-field', {
      labelPosition: labelPosition !== 'end' ? labelPosition : undefined,
      labelSpacing: resolvedLabelSpacing !== 'hug' ? resolvedLabelSpacing : undefined
    }), stylex.props(width != null && dynamicWidthStyles.width(width), xstyle), className, style),
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        // Interaction (hover/focus) listeners for the disabled-message
        // tooltip attach to the whole row for a larger trigger target;
        // positioning anchors on the switch itself (above) so the tooltip
        // appears next to the control, not the far edge of the row.
        // Handlers are gated internally by isEnabled, so attaching
        // unconditionally is safe.
        disabledMessageTooltip.interactionRef(el);
      },
      ...stylex.props(styles.container, resolvedLabelSpacing === 'spread' && styles.containerSpread, !isDisabled && switchScope),
      children: [' ', labelPosition === 'start' ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [labelElement, switchElement]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [switchElement, labelElement]
      })]
    }), status?.message && /*#__PURE__*/_jsx("div", {
      ...{
        className: "xtbrsbv"
      },
      children: /*#__PURE__*/_jsx(FieldStatus, {
        type: status.type,
        message: status.message,
        id: statusMessageID,
        variant: "detached"
      })
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
Switch.displayName = 'Switch';